if (!customElements.get('hm-collection-spotlight-carousel')) {
  class HmCollectionSpotlightCarousel extends HTMLElement {
    constructor() {
      super();

      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.desktopMediaQuery = window.matchMedia('(min-width: 992px)');

      this.isPointerDown = false;
      this.isDragging = false;
      this.isDesktopDragBound = false;
      this.startX = 0;
      this.startScrollLeft = 0;
      this.dragThreshold = 8;

      this.onNextClick = this.onNextClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.preventNativeDrag = this.preventNativeDrag.bind(this);
    }

    connectedCallback() {
      if (!this.viewport || !this.nextButton || !this.slides.length) return;

      this.nextButton.addEventListener('click', this.onNextClick);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('dragstart', this.preventNativeDrag);

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.viewport);

      if (this.desktopMediaQuery.addEventListener) {
        this.desktopMediaQuery.addEventListener('change', this.onBreakpointChange);
      } else {
        this.desktopMediaQuery.addListener(this.onBreakpointChange);
      }

      this.syncDragBehavior();
      this.nextButton.style.transformOrigin = 'center center';
      this.updateButtonState();
    }

    disconnectedCallback() {
      if (!this.viewport || !this.nextButton) return;

      this.nextButton.removeEventListener('click', this.onNextClick);
      this.viewport.removeEventListener('scroll', this.onScroll);
      this.viewport.removeEventListener('dragstart', this.preventNativeDrag);
      this.disableDesktopDrag();

      if (this.desktopMediaQuery.removeEventListener) {
        this.desktopMediaQuery.removeEventListener('change', this.onBreakpointChange);
      } else {
        this.desktopMediaQuery.removeListener(this.onBreakpointChange);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    onBreakpointChange() {
      this.syncDragBehavior();
    }

    syncDragBehavior() {
      if (this.desktopMediaQuery.matches) {
        if (this.isDesktopDragBound) return;

        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('pointermove', this.onPointerMove);
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerUp);
        this.isDesktopDragBound = true;
        return;
      }

      this.disableDesktopDrag();
    }

    disableDesktopDrag() {
      if (this.isDesktopDragBound) {
        this.viewport.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport.removeEventListener('pointermove', this.onPointerMove);
        this.viewport.removeEventListener('pointerup', this.onPointerUp);
        this.viewport.removeEventListener('pointercancel', this.onPointerUp);
        this.isDesktopDragBound = false;
      }

      this.isPointerDown = false;

      if (!this.isDragging) return;

      this.isDragging = false;
      this.viewport.classList.remove('is-dragging');
    }

    onNextClick(event) {
      event.preventDefault();

      const firstSlide = this.slides[0];
      if (!firstSlide) return;

      const maxScrollLeft = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth - 1);
      const atEnd = this.viewport.scrollLeft >= maxScrollLeft;

      if (atEnd) {
        this.viewport.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
        return;
      }

      const currentSlide =
        this.slides.find((slide) => slide.offsetLeft + slide.clientWidth / 2 > this.viewport.scrollLeft) || firstSlide;

      const nextSlide =
        currentSlide.nextElementSibling && currentSlide.nextElementSibling.matches('[data-carousel-slide]')
          ? currentSlide.nextElementSibling
          : currentSlide;

      this.viewport.scrollTo({
        left: nextSlide.offsetLeft,
        behavior: 'smooth'
      });
    }

    onScroll() {
      this.updateButtonState();
    }

    onResize() {
      this.updateButtonState();
    }

    onPointerDown(event) {
      if (!this.desktopMediaQuery.matches) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.isPointerDown = true;
      this.isDragging = false;
      this.startX = event.clientX;
      this.startScrollLeft = this.viewport.scrollLeft;
    }

    onPointerMove(event) {
      if (!this.desktopMediaQuery.matches || !this.isPointerDown) return;

      const distance = event.clientX - this.startX;

      if (!this.isDragging && Math.abs(distance) > this.dragThreshold) {
        this.isDragging = true;
        this.viewport.classList.add('is-dragging');
      }

      if (!this.isDragging) return;

      event.preventDefault();
      this.viewport.scrollLeft = this.startScrollLeft - distance;
    }

    onPointerUp() {
      this.isPointerDown = false;

      if (!this.isDragging) return;

      this.isDragging = false;
      this.viewport.classList.remove('is-dragging');
    }

    preventNativeDrag(event) {
      event.preventDefault();
    }

    updateButtonState() {
      const maxScrollLeft = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth - 1);
      const atEnd = this.viewport.scrollLeft >= maxScrollLeft;
      const canScroll = maxScrollLeft > 0;

      if (!canScroll) {
        this.nextButton.setAttribute('disabled', 'disabled');
        this.nextButton.removeAttribute('data-carousel-return');
        this.nextButton.setAttribute('aria-label', 'Show next product');
        this.nextButton.style.transform = 'translateY(-50%)';
        return;
      }

      this.nextButton.removeAttribute('disabled');

      if (atEnd) {
        this.nextButton.setAttribute('data-carousel-return', 'true');
        this.nextButton.setAttribute('aria-label', 'Back to first product');
        this.nextButton.style.transform = 'translateY(-50%) scaleX(-1)';
      } else {
        this.nextButton.removeAttribute('data-carousel-return');
        this.nextButton.setAttribute('aria-label', 'Show next product');
        this.nextButton.style.transform = 'translateY(-50%)';
      }
    }
  }

  customElements.define('hm-collection-spotlight-carousel', HmCollectionSpotlightCarousel);
}
